// CartingTool.js
import React, { useRef, useState, useEffect } from "react";
import Layout from "./Layout";
import useTableFunction from "../../../hooks/useTableFunction";
import { COLLECTION } from "../../../data/constant";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { db, storage } from "../../../config/firebase";
import { transactionTableHeading } from "../../../assets/lib/cartingTool";
import { 
  doc, 
  where, 
  getDoc, 
  setDoc, 
  updateDoc, 
  addDoc, 
  getDocs, 
  getCountFromServer,
  collection,
  query
} from "firebase/firestore";


import moment from "moment";

const CartingTool = () => {
  const dynamicConditionCB = async () => {
    return {
      column: "authStatus",
      operator: "==",
      query: "AUTH1",
    };
  };

  const {
    tableData,
    isTableLoading,
    currentPage,
    totalLength,
    handlePageChange,
    handlePageSize,
    handleSearchQuery,
    handleTableSorting,
    handleSearchDate,
  } = useTableFunction({
    collectionPath: COLLECTION.TRANSACTIONS,
    dynamicCondition: dynamicConditionCB,
    defaultOrderBy: {
      field: "transactionTime",
      direction: "desc",
    },
  });

  // Image / modal states (unchanged)
  const [images, setImages] = useState("");
  const [updateTransId, setUpdateTransId] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [currentImagesfirst, setCurrentImagesfirst] = useState(null);
  const [currentImages, setCurrentImages] = useState(null);
  const [cameraGroup, setCameraGroup] = useState([]);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addedItems, setAddedItems] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fromDate, setFromDate] = useState(moment());
  const [toDate, setToDate] = useState(moment());
  const isCloseCalledRef = useRef(false);
  const updateTransIdRef = useRef(0);
  const [MachineID, setMachineID] = useState("");

  // NEW: dispute map and enriched data
  const [disputeMap, setDisputeMap] = useState({}); // { [transactionId]: { status: 'active'|'resolved', docId, docData } }
  const [enrichedTableData, setEnrichedTableData] = useState([]);

  // --- KEEP your existing functions (groupAndSortItems, displayImageSequentially, fetchImages etc.)
  const groupAndSortItems = (items) => {
    const validItems = items.filter((item) => typeof item === "string");
    const sortedItems = validItems.sort((a, b) => {
      const numA = parseInt(a.split("_")[0]);
      const numB = parseInt(b.split("_")[0]);
      return numA - numB;
    });
    const groupedItems = {};
    sortedItems.forEach((item) => {
      const parts = item.split("_");
      const key = parts[1];
      if (!groupedItems[key]) {
        groupedItems[key] = [];
      }
      groupedItems[key].push(item);
    });
    const resultArray = Object.values(groupedItems);
    setImages(resultArray);
    return resultArray;
  };

  const displayImageSequentiallyfirst = async (
    startIndex,
    group,
    transactionRef,
    cameraNum
  ) => {
    if (isCloseCalledRef.current) {
      return;
    }
    const imageName = group[startIndex];
    const imageUrl = await getDownloadURL(ref(transactionRef, imageName));
    const image = {
      [cameraNum]: { imageUrl, imageName },
    };
    setCurrentImagesfirst((prevState) => ({
      ...prevState,
      ...image,
    }));
    if (isModalLoading) {
      setIsModalLoading(false);
    }
    await new Promise((resolve) => setTimeout(resolve, 10));
  };

  const displayImageSequentially = async (
    startIndex,
    group,
    transactionRef,
    cameraNum
  ) => {
    for (let index = startIndex; index < group.length; index++) {
      if (isCloseCalledRef.current) {
        return;
      }
      const imageName = group[index];
      const imageUrl = await getDownloadURL(ref(transactionRef, imageName));
      const image = {
        [cameraNum]: { imageUrl, imageName },
      };
      setCurrentImages((prevState) => ({
        ...prevState,
        ...image,
      }));
      if (isModalLoading) {
        setIsModalLoading(false);
      }
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  };

  const fetchImages = async (updateTrasactionData) => {
    isCloseCalledRef.current = false;
    if (updateTrasactionData.id) {
      setIsModalLoading(true);
      updateTransIdRef.current = updateTrasactionData.id;
      const transactionRef = ref(storage, `transactions/${updateTrasactionData.id}`);
      try {
        const list = await listAll(transactionRef);
        const sortedFileNames = list.items.map((itemRef) => itemRef.name);
        const group = groupAndSortItems(sortedFileNames);
        if (group && group.length === 0) {
          setIsModalLoading(false);
          return;
        }
        setCurrentImagesfirst(null);
        setCurrentImages(null);
        setCameraGroup(group);

        const inventoryDocRef = doc(db, COLLECTION.INVENTORY, updateTrasactionData.machineId);
        const inventoryDocSnap = await getDoc(inventoryDocRef);

        if (inventoryDocSnap.exists()) {
          let allItemsArray = [];
          const inventoryData = inventoryDocSnap.data();
          Object.keys(inventoryData).forEach((item) => {
            allItemsArray.push({
              itemKey: item,
              ...inventoryData[item],
            });
          });
          setMenuItems(allItemsArray);
        }
      } catch (error) {
        console.error("Error listing files:", error);
      }
    }
  };

  const fetchCameraImagesfirst = async (cameraGroup) => {
    const updateTransId = updateTransIdRef.current;
    if (updateTransId) {
      const transactionRef = ref(storage, `transactions/${updateTransId}`);
      if (cameraGroup.length > 0) {
        await Promise.all(
          cameraGroup.map(async (camera, idx) => {
            await displayImageSequentiallyfirst(0, camera, transactionRef, idx);
          })
        );
      }
    }
  };

  const fetchCameraImages = async (cameraGroup) => {
    const updateTransId = updateTransIdRef.current;
    if (updateTransId) {
      const transactionRef = ref(storage, `transactions/${updateTransId}`);
      if (cameraGroup.length > 0) {
        await Promise.all(
          cameraGroup.map(async (camera, idx) => {
            await displayImageSequentially(0, camera, transactionRef, idx);
          })
        );
      }
    }
  };

  const onCloseModal = () => {
    isCloseCalledRef.current = true;
    setTimeout(() => {
      updateTransIdRef.current = 0;
      setCurrentImagesfirst(null);
      setCurrentImages(null);
      setIsModalLoading(false);
      setCameraGroup([]);
      setIsModalOpen(false);
    }, 500);
  };

  const onClickDateSearch = () => {
    handleSearchDate(fromDate, toDate, "transactionTime");
  };

  const handleAddItem = () => {
    setAddedItems((prevItems) => [
      ...prevItems,
      {
        id: prevItems[prevItems.length - 1].id + 1,
        selectedItem: menuItems[0],
        quantity: 0,
      },
    ]);
  };

  const handleDeleteItem = (id) => {
    if (addedItems.length > 1) {
      setAddedItems((prevItems) => prevItems.filter((item) => item.id !== id));
    }
    setValidationErrors((prevErrors) => {
      if (prevErrors.some((error) => error.id === id)) {
        return prevErrors.filter((error) => error.id !== id);
      }
      return prevErrors;
    });
  };

  const onChangeValue = (value, id) => {
    setValidationErrors((prevErrors) => {
      if (value > 0 && prevErrors.some((error) => error.id === id)) {
        return prevErrors.filter((error) => error.id !== id);
      }
      return prevErrors;
    });
    setAddedItems((prevItems) => {
      const updatedItems = prevItems.map((item) =>
        item.id === id ? { ...item, quantity: value } : item
      );
      return updatedItems;
    });
  };

  // fetchWalletAmount unchanged
  const fetchWalletAmount = async (PHONE) => {
    try {
      const userRef = collection(db, "Users");
      const userSnapshot = await getDocs(query(userRef, where("phoneNumber", "==", parseInt(PHONE))));
      if (!userSnapshot.empty) {
        const userDocSnapshot = userSnapshot.docs[0].data();
        const ans = userDocSnapshot.walletAmount;
        return ans;
      } else {
        console.log("User document not found for phone number:", PHONE);
        return null;
      }
    } catch (error) {
      console.error("Error fetching wallet amount:", error);
      return null;
    }
  };

  // -------------------------
  // NEW: Fetch disputes for all transactions in tableData and enrich rows
  // -------------------------
  const fetchDisputesForTable = async () => {
    try {
      if (!tableData || tableData.length === 0) {
        setDisputeMap({});
        setEnrichedTableData(tableData || []);
        return;
      }

      // Query disputes per transaction id (parallel)
      const promises = tableData.map(async (row) => {
        try {
          const q = query(
            collection(db, COLLECTION.TRANSACTION_DISPUTES),
            where("transactionID", "==", row.id)
          );
          const snap = await getDocs(q);
          if (snap.empty) {
            return { txId: row.id, dispute: null };
          }

          // Collate docs
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // Determine status: if any active -> active, else resolved.
          const activeDoc = docs.find((d) => d.status === "active");
          if (activeDoc) {
            return { txId: row.id, dispute: { status: "active", docId: activeDoc.id, docData: activeDoc } };
          }
          // Otherwise take the most recent (by createdAt) resolved
          const docsWithCreated = docs.map((d) => {
            const created = d.createdAt ? new Date(d.createdAt) : null;
            return { ...d, _createdDate: created };
          });
          docsWithCreated.sort((a, b) => {
            const ta = a._createdDate ? a._createdDate.getTime() : 0;
            const tb = b._createdDate ? b._createdDate.getTime() : 0;
            return tb - ta;
          });
          return { txId: row.id, dispute: { status: "resolved", docId: docsWithCreated[0].id, docData: docsWithCreated[0] } };
        } catch (err) {
          console.error("Error fetching dispute for row", row.id, err);
          return { txId: row.id, dispute: null };
        }
      });

      const results = await Promise.all(promises);
      const newMap = {};
      results.forEach((r) => {
        newMap[r.txId] = r.dispute || null;
      });

      setDisputeMap(newMap);

      // Create enriched table data (add _dispute prop)
      const enriched = (tableData || []).map((row) => {
        return { ...row, _dispute: newMap[row.id] || null };
      });
      setEnrichedTableData(enriched);
    } catch (err) {
      console.error("Error in fetchDisputesForTable:", err);
    }
  };

  // fetch disputes whenever raw tableData changes
  useEffect(() => {
    fetchDisputesForTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  // Expose refresh function to pass down to modal; modal will call after resolving
  const refreshDisputes = async () => {
    await fetchDisputesForTable();
  };

  // -------------------------
  // The rest of your existing handleSubmit, handleNothing etc remain unchanged.
  // I will keep them exactly as provided earlier (omitted here for brevity),
  // but include them below to keep code complete.
  // -------------------------

  const handleSubmit = async () => {
    const newValidationErrors = addedItems.filter((item) => item.quantity <= 0);
    setValidationErrors(newValidationErrors);
    const isQuantityValid = newValidationErrors.length === 0;

    try {
      setSubmitLoading(true);
      if (isQuantityValid && updateTransIdRef.current) {
        const documentRef = doc(db, COLLECTION.TRANSACTIONS, updateTransIdRef.current);
        const itemPrices = {};

        const transactionDocSnap = await getDoc(documentRef);
        const walletAmount = await fetchWalletAmount(transactionDocSnap.data().phoneNumber);

        await Promise.all(
          addedItems.map(async (item) => {
            const itemKey = item.selectedItem.itemKey;
            const transactionData = transactionDocSnap.data();
            const mid = transactionData.machineId;
            const inventoryDocRef = doc(db, COLLECTION.INVENTORY, mid);
            const inventoryDocSnap = await getDoc(inventoryDocRef);

            if (inventoryDocSnap.exists()) {
              const itemData = inventoryDocSnap.data();
              const selectedItemData = itemData[itemKey];

              if (selectedItemData) {
                const pricePerOne = selectedItemData.unitPrice / selectedItemData.quantity;
                const unitPrice = pricePerOne;
                const quantityTaken = parseInt(item.quantity);

                if (typeof unitPrice === "number") {
                  itemPrices[itemKey] = unitPrice;

                  await updateDoc(inventoryDocRef, {
                    [itemKey + ".quantity"]: selectedItemData.quantity - quantityTaken,
                  });

                  await updateDoc(inventoryDocRef, {
                    [itemKey + ".unitPrice"]: selectedItemData.unitPrice - quantityTaken * unitPrice,
                  });
                } else {
                  console.warn("Unit price not found for", itemKey);
                }
              } else {
                console.warn("Data not found for", itemKey);
              }
            } else {
              console.warn("Document not found for", itemKey);
            }
          })
        );

        const transactionDetails = {
          itemsPicked: {},
          totalItems: 0,
          totalTransactionamount: 0,
          itemsNames: {},
        };

        addedItems.forEach((item) => {
          const itemKey = item.selectedItem.itemKey;
          const quantity = parseInt(item.quantity);
          const unitPrice = itemPrices[itemKey] || 0;
          transactionDetails.itemsPicked[itemKey] = quantity;
          transactionDetails.totalItems += quantity;
          transactionDetails.totalTransactionamount += quantity * unitPrice;
          const itemNames = item.selectedItem.productName;
          transactionDetails.itemsNames[itemNames] = quantity;
        });

        transactionDetails.cartedTime = new Date();

        const existingSnapshot = await getDoc(documentRef);
        const existingData = existingSnapshot.data();

        const dataToInsert = { ...existingData, transactionDetails };
        await setDoc(documentRef, dataToInsert);
        const paymentHistory = transactionDetails.totalTransactionamount;
        const dataReInsert = { ...existingData, paymentHistory };
        await setDoc(documentRef, dataReInsert);

        const namestring = Object.entries(transactionDetails.itemsNames)
          .map(([key, value]) => `${key}: ${value}`)
          .join(", ");
        await updateDoc(documentRef, {
          itemsPicked: namestring,
        });
        await updateDoc(documentRef, {
          transactionAmount: transactionDetails.totalTransactionamount,
        });
        await updateDoc(documentRef, {
          cartingStatus: "Completed",
        });

        const itemsPicked = [];

        addedItems.forEach((item) => {
          const itemName = item.selectedItem.productName;
          const quantity = parseInt(item.quantity);
          const unitPrice = (item.selectedItem.unitPrice || 0) / (item.selectedItem.quantity || 1);

          itemsPicked.push({
            name: itemName,
            price: unitPrice.toString(),
            quantity: quantity.toString(),
          });
        });

        const walletDeductionsRef = collection(db, COLLECTION.WALLET_DEDUCTIONS);
        const walletDeductionData = {
          Itemspicked: itemsPicked,
          Podnumber: MachineID,
          Timestamp: new Date(),
          amountDeducted: transactionDetails.totalTransactionamount,
          transactionid: updateTransIdRef.current,
          phoneNumber: transactionDocSnap.data().phoneNumber,
          Beforeamount: walletAmount,
          Afteramount: walletAmount - transactionDetails.totalTransactionamount,
        };

        await addDoc(walletDeductionsRef, walletDeductionData);

        const phoneNumber = transactionDocSnap.data().phoneNumber;
        const userQuerySnapshot = await getDocs(query(collection(db, COLLECTION.USER), where("phoneNumber", "==", parseInt(phoneNumber))));

        if (!userQuerySnapshot.empty) {
          const userData = userQuerySnapshot.docs[0].data();
          const userId = userQuerySnapshot.docs[0].id;
          const currentWalletAmount = userData.walletAmount;

          const updatedWalletAmount = currentWalletAmount - transactionDetails.totalTransactionamount;
          const userDocRef = doc(db, COLLECTION.USER, userId);

          await updateDoc(userDocRef, { walletAmount: updatedWalletAmount });
        } else {
          console.log("User not found:", phoneNumber);
        }

        setSubmitLoading(false);
        setIsModalOpen(false);
      } else {
        console.error("Quantity must be greater than 0 for all added items");
        setSubmitLoading(false);
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error", error);
      setSubmitLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleNothing = async () => {
    const documentRef = doc(db, COLLECTION.TRANSACTIONS, updateTransIdRef.current);
    const item = "None";
    const tamount = 0;
    const status = "Completed";

    await updateDoc(documentRef, {
      itemsPicked: item,
    });

    await updateDoc(documentRef, {
      transactionAmount: tamount,
    });

    await updateDoc(documentRef, {
      cartingStatus: status,
    });

    isCloseCalledRef.current = true;
    setTimeout(() => {
      updateTransIdRef.current = 0;
      setCurrentImagesfirst(null);
      setCurrentImages(null);
      setIsModalLoading(false);
      setCameraGroup([]);
      setIsModalOpen(false);
    }, 500);
  };

  useEffect(() => {
    if (menuItems.length > 0) setAddedItems([{ id: 1, selectedItem: menuItems[0], quantity: 0 }]);
  }, [menuItems]);

  useEffect(() => {
    if (cameraGroup && cameraGroup.length > 0) {
      fetchCameraImagesfirst(cameraGroup);
      fetchCameraImages(cameraGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraGroup]);

  // Pass enrichedTableData (with _dispute property) into Layout
  return (
    <Layout
      transactionTableHeading={transactionTableHeading}
      transactionTableData={enrichedTableData} // <- enriched
      isTableLoading={isTableLoading}
      currentPage={currentPage}
      totalLength={totalLength}
      handlePageChange={handlePageChange}
      handlePageSize={handlePageSize}
      handleSearchQuery={handleSearchQuery}
      handleTableSorting={handleTableSorting}
      updateTransId={updateTransId}
      setUpdateTransId={setUpdateTransId}
      fetchImages={fetchImages}
      currentImages={currentImages}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      onCloseModal={onCloseModal}
      isModalLoading={isModalLoading}
      menuItems={menuItems}
      addedItems={addedItems}
      setAddedItems={setAddedItems}
      handleAddItem={handleAddItem}
      handleDeleteItem={handleDeleteItem}
      onChangeValue={onChangeValue}
      handleSubmit={handleSubmit}
      validationErrors={validationErrors}
      submitLoading={submitLoading}
      fromDate={fromDate}
      setFromDate={setFromDate}
      toDate={toDate}
      setToDate={setToDate}
      onClickDateSearch={onClickDateSearch}
      images={images}
      currentImagesfirst={currentImagesfirst}
      handleNothing={handleNothing}
      refreshDisputes={refreshDisputes} // provide refresh to layout/modal
    />
  );
};

export default CartingTool;
